
import { useEffect, useState } from "react";
import { Select } from "antd";
import Cookies from "js-cookie";
import { Link, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useUserPermissions } from "../../../UserPermissionsContext";
import Swal from "sweetalert2";
import { all_routes } from "../../../router/all_routes";
interface ClassItem {
    class_name_id: number;
    name: string;
}

interface OrientationItem {
    orientation_id: number;
    name: string;
}

interface ApiRecord {
    id: number;
    branch: number;
    branch_class: number | null;
    orientation: number | null;
    is_finalized: boolean;
    is_active: boolean;
}

type ApiResponse = ApiRecord[];

interface RowData {
    recordId?: number;
    branch_class: number | null;
    orientation: number | null;
    is_finalized?: boolean;
    isNew?: boolean;
    hasError?: boolean;
}

const WizklubBranchClassOrientation = () => {
    const [searchParams] = useSearchParams();
    const routes = all_routes;
    const branchId = searchParams.get("id");
    const branchName = searchParams.get("branch_name");
    const { webApi } = useUserPermissions();

    const jwtToken = Cookies.get("authToken");

    const [rows, setRows] = useState<RowData[]>([]);

    const [classList, setClassList] = useState<ClassItem[]>([]);

    const [orientationList, setOrientationList] = useState<
        OrientationItem[]
    >([]);

    const [loading, setLoading] = useState(false);

    const [updatingRowIndex, setUpdatingRowIndex] =
        useState<number | null>(null);

    const [addingRow, setAddingRow] = useState(false);
    const [finalizing, setFinalizing] = useState(false);
    const [showFinalizeModal, setShowFinalizeModal] = useState(false);
    /*
     * ---------------------------------------------------------
     * COMMON API ERROR TOAST
     * ---------------------------------------------------------
     *
     * Handles API responses such as:
     *
     * {
     *     "branch_class": [
     *         "This class is already selected."
     *     ],
     *     "orientation": [
     *         "This orientation is already selected."
     *     ]
     * }
     *
     * Also handles:
     *
     * {
     *     "message": "Something went wrong"
     * }
     *
     * {
     *     "detail": "Something went wrong"
     * }
     */
    const handleFinalize = async () => {
        if (!branchId) {
            await Swal.fire({
                icon: "error",
                title: "Branch ID Missing",
                text: "Branch ID is missing. Unable to finalize.",
                confirmButtonText: "OK",
            });
            return;
        }

        /*
         * Check for empty/null Class or Orientation
         */
        const invalidRows = rows.filter(
            (row) =>
                row.branch_class === null ||
                row.orientation === null
        );

        /*
         * Do NOT finalize if any row is incomplete
         */
        if (invalidRows.length > 0) {
            await Swal.fire({
                icon: "warning",
                title: "Incomplete Selection",
                text: "Please select both Class and Orientation for all records before finalizing.",
                confirmButtonText: "OK",
            });

            return;
        }

        /*
         * Confirmation popup
         */
        const result = await Swal.fire({
            icon: "question",
            title: "Finalize Selection?",
            text: "Are you sure you want to finalize the class and orientation selections?",
            showCancelButton: true,
            confirmButtonText: "Yes, Finalize",
            cancelButtonText: "No",
            reverseButtons: true,
        });

        /*
         * User clicked No
         */
        if (!result.isConfirmed) {
            return;
        }

        setFinalizing(true);

        try {
            const response = await fetch(
                `${webApi}/wizklub/finalize_wizklub_branch_class_orientation_selection/?branch_id=${branchId}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${jwtToken}`,
                    },
                }
            );

            const data = await response.json();

            if (response.ok) {
                await Swal.fire({
                    icon: "success",
                    title: "Finalized Successfully",
                    text:
                        data?.message ||
                        "Class and Orientation selections finalized successfully.",
                    confirmButtonText: "OK",
                });

                /*
                 * Refresh data after successful finalization
                 */
                await getBranchClassOrientation();
            } else {
                const errorMessage =
                    data?.message ||
                    data?.detail ||
                    "Unable to finalize class and orientation selections.";

                await Swal.fire({
                    icon: "error",
                    title: "Finalization Failed",
                    text: errorMessage,
                    confirmButtonText: "OK",
                });
            }
        } catch (error) {
            console.error(
                "Error finalizing class & orientation:",
                error
            );

            await Swal.fire({
                icon: "error",
                title: "Something Went Wrong",
                text: "Unable to finalize the selections. Please try again.",
                confirmButtonText: "OK",
            });
        } finally {
            setFinalizing(false);
        }
    };
    const showApiError = (
        data: any,
        defaultMessage: string
    ) => {
        if (!data) {
            toast.error(defaultMessage);
            return;
        }

        /*
         * message
         */
        if (
            typeof data.message === "string" &&
            data.message.trim()
        ) {
            toast.error(data.message);
            return;
        }

        /*
         * detail
         */
        if (
            typeof data.detail === "string" &&
            data.detail.trim()
        ) {
            toast.error(data.detail);
            return;
        }

        /*
         * Key / Value validation errors
         */
        if (
            typeof data === "object" &&
            !Array.isArray(data)
        ) {
            const errorMessages: string[] = [];

            Object.entries(data).forEach(
                ([key, value]) => {
                    if (Array.isArray(value)) {
                        value.forEach((message) => {
                            if (
                                typeof message ===
                                "string"
                            ) {
                                errorMessages.push(
                                    `${key}: ${message}`
                                );
                            } else {
                                errorMessages.push(
                                    `${key}: ${JSON.stringify(
                                        message
                                    )}`
                                );
                            }
                        });
                    } else if (
                        typeof value === "string" ||
                        typeof value === "number"
                    ) {
                        errorMessages.push(
                            `${key}: ${String(value)}`
                        );
                    } else if (value !== null) {
                        errorMessages.push(
                            `${key}: ${JSON.stringify(
                                value
                            )}`
                        );
                    }
                }
            );

            if (errorMessages.length > 0) {
                errorMessages.forEach((message) => {
                    toast.error(message);
                });

                return;
            }
        }

        toast.error(defaultMessage);
    };

    /*
     * ---------------------------------------------------------
     * GET CLASS LIST
     * ---------------------------------------------------------
     */
    const getClassList = async () => {
        try {
            const response = await fetch(
                `${webApi}/wizklub/classes_dropdown_for_branch_selection/`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type":
                            "application/json",
                        Authorization: `Bearer ${jwtToken}`,
                    },
                }
            );

            const data = await response.json();

            if (response.ok) {
                setClassList(data || []);
            } else {
                console.error(
                    "Class API error:",
                    data
                );
            }
        } catch (error) {
            console.error(
                "Error fetching classes:",
                error
            );
        }
    };

    /*
     * ---------------------------------------------------------
     * GET ORIENTATION LIST
     * ---------------------------------------------------------
     */
    const getOrientationList = async () => {
        if (!branchId) return;

        try {
            const response = await fetch(
                `${webApi}/wizklub/orientations_dropdown_for_branch_selection/?branch_id=${branchId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type":
                            "application/json",
                        Authorization: `Bearer ${jwtToken}`,
                    },
                }
            );

            const data = await response.json();

            if (response.ok) {
                setOrientationList(data || []);
            } else {
                console.error(
                    "Orientation API error:",
                    data
                );
            }
        } catch (error) {
            console.error(
                "Error fetching orientations:",
                error
            );
        }
    };

    /*
     * ---------------------------------------------------------
     * GET EXISTING RECORDS
     * ---------------------------------------------------------
     */
    const getBranchClassOrientation =
        async () => {
            if (!branchId) return;

            setLoading(true);

            try {
                const response = await fetch(
                    `${webApi}/wizklub/create_update_wizklub_branch_class_orientation_selection/?branch_id=${branchId}`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type":
                                "application/json",
                            Authorization: `Bearer ${jwtToken}`,
                        },
                    }
                );

                const data: ApiResponse =
                    await response.json();

                if (
                    response.ok &&
                    Array.isArray(data)
                ) {
                    /*
                     * One API record = one UI row.
                     */
                    const formattedRows: RowData[] = data
                        .filter((item) => item.is_active !== false)
                        .map((item) => ({
                            recordId: item.id,
                            branch_class: item.branch_class,
                            orientation: item.orientation,
                            is_finalized: item.is_finalized,
                            isNew: false,
                            hasError: false,
                        }));

                    setRows(formattedRows);
                } else {
                    setRows([]);
                }
            } catch (error) {
                console.error(
                    "Error fetching class & orientation:",
                    error
                );

                setRows([]);
            } finally {
                setLoading(false);
            }
        };

    /*
     * ---------------------------------------------------------
     * INITIAL LOAD
     * ---------------------------------------------------------
     */
    useEffect(() => {
        getClassList();
    }, []);

    useEffect(() => {
        if (branchId) {
            getOrientationList();
            getBranchClassOrientation();
        }
    }, [branchId]);

    /*
     * ---------------------------------------------------------
     * CREATE EMPTY RECORD
     * ---------------------------------------------------------
     *
     * Clicking +
     *
     * POST
     *
     * {
     *     branch: branchId,
     *     branch_class: null,
     *     orientation: null,
     *     is_active: true
     * }
     *
     * After successful POST, the new record is
     * immediately added as the last UI row.
     */
    const handleAddRow = async () => {
        if (!branchId) {
            toast.error("Branch ID is missing");
            return;
        }

        if (addingRow) return;

        setAddingRow(true);

        try {
            const response = await fetch(
                `${webApi}/wizklub/create_update_wizklub_branch_class_orientation_selection/?branch_id=${branchId}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type":
                            "application/json",
                        Authorization: `Bearer ${jwtToken}`,
                    },
                    body: JSON.stringify({
                        branch: Number(branchId),
                        branch_class: null,
                        orientation: null,
                        is_active: true,
                    }),
                }
            );

            const data = await response.json();

            if (response.ok) {
                /*
                 * Different APIs may return:
                 *
                 * { id: 10 }
                 *
                 * or:
                 *
                 * { result: { id: 10 } }
                 *
                 * or:
                 *
                 * { data: { id: 10 } }
                 */
                const newRecordId =
                    data?.id ??
                    data?.result?.id ??
                    data?.data?.id;

                if (newRecordId) {
                    setRows((prevRows) => [
                        ...prevRows,
                        {
                            recordId: newRecordId,
                            branch_class: null,
                            orientation: null,
                            isNew: true,
                            hasError: false,
                        },
                    ]);
                } else {
                    /*
                     * If POST doesn't return ID,
                     * GET again to obtain it.
                     */
                    await getBranchClassOrientation();
                }

                toast.success(
                    data?.message ||
                    "New record added successfully"
                );
            } else {
                console.error(
                    "Create empty record error:",
                    data
                );

                showApiError(
                    data,
                    "Unable to create record"
                );
            }
        } catch (error) {
            console.error(
                "Error creating empty record:",
                error
            );

            toast.error(
                "Something went wrong while creating the record"
            );
        } finally {
            setAddingRow(false);
        }
    };

    /*
     * ---------------------------------------------------------
     * UPDATE ROW
     * ---------------------------------------------------------
     *
     * SAME recordId is used for both Class and Orientation.
     *
     * Example:
     *
     * recordId = 15
     *
     * Class change:
     * PUT /15/
     *
     * Orientation change:
     * PUT /15/
     */
    const updateRow = async (
        row: RowData,
        index: number,
        updatedValues: {
            branch_class: number | null;
            orientation: number | null;
        }
    ) => {
        if (!branchId) {
            toast.error("Branch ID is missing");
            return;
        }

        if (!row.recordId) {
            toast.error(`Record ID is missing for row ${index + 1}`);
            return;
        }

        setUpdatingRowIndex(index);

        try {
            const response = await fetch(
                `${webApi}/wizklub/create_update_wizklub_branch_class_orientation_selection/${row.recordId}/?branch_id=${branchId}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${jwtToken}`,
                    },
                    body: JSON.stringify({
                        branch: Number(branchId),
                        branch_class: updatedValues.branch_class,
                        orientation: updatedValues.orientation,
                        is_active: true,
                    }),
                }
            );

            const data = await response.json();

            if (response.ok) {
                /*
                 * Remove error highlighting after
                 * successful API update.
                 */
                setRows((prevRows) =>
                    prevRows.map((item, rowIndex) =>
                        rowIndex === index
                            ? {
                                ...item,
                                branch_class:
                                    updatedValues.branch_class,
                                orientation:
                                    updatedValues.orientation,
                                hasError: false,
                            }
                            : item
                    )
                );

                toast.success(
                    data?.message ||
                    "Record updated successfully"
                );
            } else {
                /*
                 * Mark this particular row as ERROR.
                 */
                setRows((prevRows) =>
                    prevRows.map((item, rowIndex) =>
                        rowIndex === index
                            ? {
                                ...item,
                                hasError: true,
                            }
                            : item
                    )
                );

                console.error(
                    "Update row error:",
                    data
                );

                showApiError(
                    data,
                    "Unable to update record"
                );
            }
        } catch (error) {
            /*
             * Mark row as ERROR for network/server error.
             */
            setRows((prevRows) =>
                prevRows.map((item, rowIndex) =>
                    rowIndex === index
                        ? {
                            ...item,
                            hasError: true,
                        }
                        : item
                )
            );

            console.error(
                "Error updating row:",
                error
            );

            toast.error(
                "Something went wrong while updating the record"
            );
        } finally {
            setUpdatingRowIndex(null);
        }
    };

    /*
     * ---------------------------------------------------------
     * CLASS CHANGE
     * ---------------------------------------------------------
     */
    const handleClassChange = (
        index: number,
        value: number | null
    ) => {
        const currentRow = rows[index];

        if (!currentRow) return;

        /*
         * Update UI immediately.
         */
        setRows((prevRows) =>
            prevRows.map(
                (row, rowIndex) =>
                    rowIndex === index
                        ? {
                            ...row,
                            branch_class:
                                value,
                        }
                        : row
            )
        );

        /*
         * Automatically PUT.
         *
         * Orientation from the same row is preserved.
         */
        updateRow(currentRow, index, {
            branch_class: value,
            orientation:
                currentRow.orientation,
        });
    };

    /*
     * ---------------------------------------------------------
     * ORIENTATION CHANGE
     * ---------------------------------------------------------
     */
    const handleOrientationChange = (
        index: number,
        value: number | null
    ) => {
        const currentRow = rows[index];

        if (!currentRow) return;

        /*
         * Update UI immediately.
         */
        setRows((prevRows) =>
            prevRows.map(
                (row, rowIndex) =>
                    rowIndex === index
                        ? {
                            ...row,
                            orientation:
                                value,
                        }
                        : row
            )
        );

        /*
         * Automatically PUT.
         *
         * Class from the same row is preserved.
         */
        updateRow(currentRow, index, {
            branch_class:
                currentRow.branch_class,
            orientation: value,
        });
    };

    /*
     * ---------------------------------------------------------
     * DELETE / DEACTIVATE ROW
     * ---------------------------------------------------------
     *
     * PUT
     *
     * {
     *     branch: branchId,
     *     branch_class: null,
     *     orientation: null,
     *     is_active: false
     * }
     */
    const handleDeleteRow = async (
        row: RowData,
        index: number
    ) => {
        if (!row.recordId) {
            setRows((prevRows) =>
                prevRows.filter(
                    (_, rowIndex) =>
                        rowIndex !== index
                )
            );

            return;
        }

        if (!branchId) {
            toast.error("Branch ID is missing");
            return;
        }

        // const confirmDelete =
        //     // window.confirm(
        //     //     "Are you sure you want to deactivate this record?"
        //     // );

        // if (!confirmDelete) return;

        setUpdatingRowIndex(index);

        try {
            const response = await fetch(
                `${webApi}/wizklub/create_update_wizklub_branch_class_orientation_selection/${row.recordId}/?branch_id=${branchId}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type":
                            "application/json",
                        Authorization: `Bearer ${jwtToken}`,
                    },
                    body: JSON.stringify({
                        branch: Number(branchId),
                        branch_class: null,
                        orientation: null,
                        is_active: false,
                    }),
                }
            );

            const data = await response.json();

            if (response.ok) {
                /*
                 * Remove from UI.
                 */
                setRows((prevRows) =>
                    prevRows.filter(
                        (_, rowIndex) =>
                            rowIndex !== index
                    )
                );

                toast.success(
                    data?.message ||
                    "Record deleted successfully"
                );
            } else {
                console.error(
                    "Delete error:",
                    data
                );

                showApiError(
                    data,
                    "Unable to deactivate record"
                );
            }
        } catch (error) {
            console.error(
                "Error deleting row:",
                error
            );

            toast.error(
                "Something went wrong while deleting the record"
            );
        } finally {
            setUpdatingRowIndex(null);
        }
    };

    /*
     * ---------------------------------------------------------
     * CLASS SEARCH
     * ---------------------------------------------------------
     */
    const filterClassOption = (
        input: string,
        option?: {
            label?: string;
            value?: number;
        }
    ) => {
        return (
            option?.label
                ?.toLowerCase()
                .includes(
                    input.toLowerCase()
                ) ?? false
        );
    };

    /*
     * ---------------------------------------------------------
     * ORIENTATION SEARCH
     * ---------------------------------------------------------
     */
    const filterOrientationOption = (
        input: string,
        option?: {
            label?: string;
            value?: number;
        }
    ) => {
        return (
            option?.label
                ?.toLowerCase()
                .includes(
                    input.toLowerCase()
                ) ?? false
        );
    };

    return (
        <>
            {/* =================================================
                TOAST CONTAINER
            ================================================= */}
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
                draggable
            />

            <div className="page-wrapper">
                <div className="content">

                    {/* =================================================
                        PAGE HEADER
                    ================================================= */}
                    <div className="page-header mb-3">
                        <div className="row align-items-center">
                            <div className="col">
                                <h3 className="page-title mb-1">
                                    Branch Class & Orientation <span style={{ color: "#87CEEB" }}>
                                        ({branchName || ""})
                                    </span>
                                </h3>
                                <p
                                    className="text-muted mb-0"
                                    style={{
                                        fontSize:
                                            "13px",
                                    }}
                                >
                                    Select class and
                                    orientation for
                                    this branch.
                                </p>
                                <nav>
                                    <ol className="breadcrumb mb-0">
                                        <li className="breadcrumb-item">
                                            <Link to={routes.adminDashboard1}>Dashboard</Link>
                                        </li>
                                        <li className="breadcrumb-item active" aria-current="page">
                                           <Link to={routes.wizklubBranchesTeamLead}>Branches</Link>
                                        </li>
                                    </ol>
                                </nav>
                            </div>
                        </div>
                    </div>

                    {/* =================================================
                        MAIN CARD
                    ================================================= */}
                    <div
                        className="card"
                        style={{
                            borderRadius:
                                "10px",
                            border:
                                "1px solid #e5e7eb",
                            boxShadow:
                                "0 2px 8px rgba(0, 0, 0, 0.04)",
                        }}
                    >
                        {/* CARD HEADER */}
                        <div
                            className="card-header bg-white"
                            style={{
                                padding:
                                    "16px 20px",
                                borderBottom:
                                    "1px solid #eeeeee",
                            }}
                        >
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h5
                                        className="mb-1"
                                        style={{
                                            fontSize:
                                                "16px",
                                            fontWeight:
                                                600,
                                        }}
                                    >
                                        Class &
                                        Orientation
                                    </h5>

                                    <span
                                        className="text-muted"
                                        style={{
                                            fontSize:
                                                "12px",
                                        }}
                                    >
                                        {rows.length ===
                                            0
                                            ? "No records added"
                                            : `${rows.length} ${rows.length ===
                                                1
                                                ? "record"
                                                : "records"
                                            }`}
                                    </span>
                                </div>

                                {rows.length >
                                    0 && (
                                        <span
                                            style={{
                                                display:
                                                    "inline-flex",
                                                alignItems:
                                                    "center",
                                                gap: "5px",
                                                padding:
                                                    "5px 10px",
                                                borderRadius:
                                                    "20px",
                                                fontSize:
                                                    "12px",
                                                color:
                                                    "#389e0d",
                                                background:
                                                    "#f6ffed",
                                                border:
                                                    "1px solid #b7eb8f",
                                            }}
                                        >
                                            <i className="ti ti-check" />
                                            Auto-saved
                                        </span>
                                    )}
                            </div>
                        </div>

                        {/* TABLE */}
                        <div
                            className="card-body"
                            style={{
                                padding: 0,
                            }}
                        >
                            <div className="table-responsive">
                                <table
                                    className="table mb-0"
                                    style={{
                                        minWidth:
                                            "700px",
                                    }}
                                >
                                    <thead>
                                        <tr
                                            style={{
                                                background:
                                                    "#fafafa",
                                            }}
                                        >
                                            <th
                                                style={{
                                                    width:
                                                        "70px",
                                                    padding:
                                                        "14px 20px",
                                                    fontSize:
                                                        "12px",
                                                    fontWeight:
                                                        600,
                                                    color:
                                                        "#666",
                                                    borderBottom:
                                                        "1px solid #eeeeee",
                                                }}
                                            >
                                                S.No
                                            </th>

                                            <th
                                                style={{
                                                    padding:
                                                        "14px 10px",
                                                    fontSize:
                                                        "12px",
                                                    fontWeight:
                                                        600,
                                                    color:
                                                        "#666",
                                                    borderBottom:
                                                        "1px solid #eeeeee",
                                                }}
                                            >
                                                Class
                                            </th>

                                            <th
                                                style={{
                                                    padding:
                                                        "14px 10px",
                                                    fontSize:
                                                        "12px",
                                                    fontWeight:
                                                        600,
                                                    color:
                                                        "#666",
                                                    borderBottom:
                                                        "1px solid #eeeeee",
                                                }}
                                            >
                                                Orientation
                                            </th>

                                            <th
                                                style={{
                                                    width:
                                                        "110px",
                                                    padding:
                                                        "14px 20px",
                                                    fontSize:
                                                        "12px",
                                                    fontWeight:
                                                        600,
                                                    color:
                                                        "#666",
                                                    textAlign:
                                                        "right",
                                                    borderBottom:
                                                        "1px solid #eeeeee",
                                                }}
                                            >
                                                Action
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {/* =================================================
                                            LOADING
                                        ================================================= */}
                                        {loading ? (
                                            <tr>
                                                <td
                                                    colSpan={
                                                        4
                                                    }
                                                    className="text-center"
                                                    style={{
                                                        padding:
                                                            "60px 20px",
                                                    }}
                                                >
                                                    <div
                                                        className="spinner-border text-primary mb-2"
                                                        role="status"
                                                    />

                                                    <div
                                                        className="text-muted"
                                                        style={{
                                                            fontSize:
                                                                "13px",
                                                        }}
                                                    >
                                                        Loading...
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : rows.length ===
                                            0 ? (
                                            /* =================================================
                                                EMPTY STATE
                                            ================================================= */
                                            <tr>
                                                <td
                                                    style={{
                                                        padding:
                                                            "25px 20px",
                                                        color:
                                                            "#777",
                                                        fontSize:
                                                            "13px",
                                                    }}
                                                >
                                                    —
                                                </td>

                                                <td
                                                    colSpan={
                                                        2
                                                    }
                                                    style={{
                                                        padding:
                                                            "25px 10px",
                                                    }}
                                                >
                                                    <div
                                                        className="d-flex align-items-center"
                                                        style={{
                                                            gap:
                                                                "10px",
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                width:
                                                                    "36px",
                                                                height:
                                                                    "36px",
                                                                borderRadius:
                                                                    "50%",
                                                                background:
                                                                    "#f0f5ff",
                                                                display:
                                                                    "flex",
                                                                alignItems:
                                                                    "center",
                                                                justifyContent:
                                                                    "center",
                                                            }}
                                                        >
                                                            <i
                                                                className="ti ti-school"
                                                                style={{
                                                                    fontSize:
                                                                        "18px",
                                                                    color:
                                                                        "#1677ff",
                                                                }}
                                                            />
                                                        </div>

                                                        <div>
                                                            <div
                                                                style={{
                                                                    fontSize:
                                                                        "14px",
                                                                    fontWeight:
                                                                        500,
                                                                    color:
                                                                        "#333",
                                                                }}
                                                            >
                                                                No
                                                                class
                                                                added
                                                            </div>

                                                            <div
                                                                className="text-muted"
                                                                style={{
                                                                    fontSize:
                                                                        "12px",
                                                                }}
                                                            >
                                                                Click
                                                                +
                                                                to
                                                                add
                                                                a
                                                                class
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* ADD WHEN EMPTY */}
                                                <td
                                                    style={{
                                                        padding:
                                                            "25px 20px",
                                                        textAlign:
                                                            "right",
                                                    }}
                                                >
                                                    <button
                                                        type="button"
                                                        title="Add new class"
                                                        className="btn btn-primary"
                                                        disabled={
                                                            addingRow
                                                        }
                                                        onClick={
                                                            handleAddRow
                                                        }
                                                        style={{
                                                            width:
                                                                "38px",
                                                            height:
                                                                "38px",
                                                            padding:
                                                                0,
                                                            borderRadius:
                                                                "6px",
                                                            display:
                                                                "inline-flex",
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "center",
                                                        }}
                                                    >
                                                        {addingRow ? (
                                                            <span
                                                                className="spinner-border spinner-border-sm"
                                                                role="status"
                                                            />
                                                        ) : (
                                                            <i className="ti ti-plus" />
                                                        )}
                                                    </button>
                                                </td>
                                            </tr>
                                        ) : (
                                            /* =================================================
                                                RECORDS
                                            ================================================= */
                                            rows.map(
                                                (
                                                    row,
                                                    index
                                                ) => (
                                                    <tr
                                                        key={row.recordId ?? `row-${index}`}
                                                        style={{
                                                            verticalAlign: "middle",

                                                            // Error row gets light red
                                                            // Finalized row gets light green
                                                            backgroundColor: row.hasError
                                                                ? "#fff1f0"
                                                                : row.is_finalized
                                                                    ? "#f6ffed"
                                                                    : "transparent",

                                                            borderLeft: row.hasError
                                                                ? "4px solid #ff4d4f"
                                                                : row.is_finalized
                                                                    ? "4px solid #52c41a"
                                                                    : "4px solid transparent",

                                                            borderRight: row.hasError
                                                                ? "1px solid #ffccc7"
                                                                : row.is_finalized
                                                                    ? "1px solid #b7eb8f"
                                                                    : undefined,
                                                        }}
                                                    >
                                                        {/* S.NO */}
                                                        <td
                                                            style={{
                                                                padding:
                                                                    "16px 20px",
                                                                borderBottom:
                                                                    index ===
                                                                        rows.length -
                                                                        1
                                                                        ? "none"
                                                                        : "1px solid #f0f0f0",
                                                            }}
                                                        >
                                                            <span
                                                                style={{
                                                                    width:
                                                                        "30px",
                                                                    height:
                                                                        "30px",
                                                                    display:
                                                                        "inline-flex",
                                                                    alignItems:
                                                                        "center",
                                                                    justifyContent:
                                                                        "center",
                                                                    borderRadius:
                                                                        "50%",
                                                                    background:
                                                                        "#f5f5f5",
                                                                    color:
                                                                        "#555",
                                                                    fontSize:
                                                                        "12px",
                                                                    fontWeight:
                                                                        500,
                                                                }}
                                                            >
                                                                {index +
                                                                    1}
                                                            </span>
                                                        </td>

                                                        {/* CLASS */}
                                                        <td
                                                            style={{
                                                                padding:
                                                                    "16px 10px",
                                                                borderBottom:
                                                                    index ===
                                                                        rows.length -
                                                                        1
                                                                        ? "none"
                                                                        : "1px solid #f0f0f0",
                                                            }}
                                                        >
                                                            <Select
                                                                showSearch
                                                                allowClear={!row.is_finalized}
                                                                disabled={row.is_finalized}
                                                                placeholder="Select Class"
                                                                size="large"
                                                                style={{
                                                                    width: "100%",
                                                                    borderRadius: "6px",
                                                                    border: row.hasError
                                                                        ? "1px solid #ff7875"
                                                                        : undefined,
                                                                }}
                                                                value={
                                                                    row.branch_class ?? undefined
                                                                }
                                                                onChange={(value) =>
                                                                    handleClassChange(
                                                                        index,
                                                                        value ?? null
                                                                    )
                                                                }
                                                                filterOption={filterClassOption}
                                                                optionFilterProp="label"
                                                                options={classList.map((item) => ({
                                                                    value: item.class_name_id,
                                                                    label: item.name,
                                                                }))}
                                                            />
                                                        </td>

                                                        {/* ORIENTATION */}
                                                        <td
                                                            style={{
                                                                padding:
                                                                    "16px 10px",
                                                                borderBottom:
                                                                    index ===
                                                                        rows.length -
                                                                        1
                                                                        ? "none"
                                                                        : "1px solid #f0f0f0",
                                                            }}
                                                        >
                                                            <Select
                                                                showSearch
                                                                allowClear={!row.is_finalized}
                                                                disabled={row.is_finalized}
                                                                placeholder="Select Orientation"
                                                                size="large"
                                                                style={{
                                                                    width: "100%",
                                                                    borderRadius: "6px",
                                                                    border: row.hasError
                                                                        ? "1px solid #ff7875"
                                                                        : undefined,
                                                                }}
                                                                value={
                                                                    row.orientation ?? undefined
                                                                }
                                                                onChange={(value) =>
                                                                    handleOrientationChange(
                                                                        index,
                                                                        value ?? null
                                                                    )
                                                                }
                                                                filterOption={filterOrientationOption}
                                                                optionFilterProp="label"
                                                                options={orientationList.map((item) => ({
                                                                    value: item.orientation_id,
                                                                    label: item.name,
                                                                }))}
                                                            />
                                                        </td>

                                                        {/* ACTION */}
                                                        <td
                                                            style={{
                                                                padding:
                                                                    "16px 20px",
                                                                borderBottom:
                                                                    index ===
                                                                        rows.length -
                                                                        1
                                                                        ? "none"
                                                                        : "1px solid #f0f0f0",
                                                            }}
                                                        >
                                                            <div
                                                                className="d-flex justify-content-end align-items-center"
                                                                style={{
                                                                    gap:
                                                                        "6px",
                                                                }}
                                                            >
                                                                {row.is_finalized && (
                                                                    <span
                                                                        style={{
                                                                            display: "inline-flex",
                                                                            alignItems: "center",
                                                                            gap: "4px",
                                                                            padding: "5px 9px",
                                                                            borderRadius: "15px",
                                                                            fontSize: "11px",
                                                                            fontWeight: 500,
                                                                            color: "#389e0d",
                                                                            background: "#f6ffed",
                                                                            border: "1px solid #b7eb8f",
                                                                        }}
                                                                    >
                                                                        <i className="ti ti-lock" />

                                                                    </span>
                                                                )}
                                                                {/* DELETE */}
                                                                <button
                                                                    type="button"
                                                                    title="Delete"
                                                                    className="btn btn-light"
                                                                    disabled={
                                                                        updatingRowIndex ===
                                                                        index
                                                                    }
                                                                    onClick={() =>
                                                                        handleDeleteRow(
                                                                            row,
                                                                            index
                                                                        )
                                                                    }
                                                                    style={{
                                                                        width:
                                                                            "38px",
                                                                        height:
                                                                            "38px",
                                                                        padding:
                                                                            0,
                                                                        borderRadius:
                                                                            "6px",
                                                                        display:
                                                                            "inline-flex",
                                                                        alignItems:
                                                                            "center",
                                                                        justifyContent:
                                                                            "center",
                                                                        color:
                                                                            "#ff4d4f",
                                                                        border:
                                                                            "1px solid #ffd6d6",
                                                                        background:
                                                                            "#fff",
                                                                    }}
                                                                >
                                                                    {updatingRowIndex ===
                                                                        index ? (
                                                                        <span
                                                                            className="spinner-border spinner-border-sm"
                                                                            role="status"
                                                                        />
                                                                    ) : (
                                                                        <i className="ti ti-trash" />
                                                                    )}
                                                                </button>

                                                                {/* =================================================
                                                                    ADD ONLY ON LAST ROW
                                                                ================================================= */}
                                                                {index ===
                                                                    rows.length -
                                                                    1 && (
                                                                        <button
                                                                            type="button"
                                                                            title="Add new class"
                                                                            className="btn btn-primary"
                                                                            disabled={
                                                                                addingRow
                                                                            }
                                                                            onClick={
                                                                                handleAddRow
                                                                            }
                                                                            style={{
                                                                                width:
                                                                                    "38px",
                                                                                height:
                                                                                    "38px",
                                                                                padding:
                                                                                    0,
                                                                                borderRadius:
                                                                                    "6px",
                                                                                display:
                                                                                    "inline-flex",
                                                                                alignItems:
                                                                                    "center",
                                                                                justifyContent:
                                                                                    "center",
                                                                            }}
                                                                        >
                                                                            {addingRow ? (
                                                                                <span
                                                                                    className="spinner-border spinner-border-sm"
                                                                                    role="status"
                                                                                />
                                                                            ) : (
                                                                                <i className="ti ti-plus" />
                                                                            )}
                                                                        </button>
                                                                    )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* FOOTER */}
                        {rows.length >
                            0 &&
                            !loading && (
                                <div
                                    style={{
                                        padding:
                                            "12px 20px",
                                        background:
                                            "#fafafa",
                                        borderTop:
                                            "1px solid #eeeeee",
                                        borderRadius:
                                            "0 0 10px 10px",
                                    }}
                                >
                                    <div
                                        className="d-flex align-items-center"
                                        style={{
                                            gap:
                                                "6px",
                                            fontSize:
                                                "12px",
                                            color:
                                                "#777",
                                        }}
                                    >
                                        <i className="ti ti-info-circle" />

                                        <span>
                                            Use the{" "}
                                            <strong>
                                                +
                                            </strong>{" "}
                                            button on
                                            the last
                                            row to
                                            add
                                            another
                                            class.
                                        </span>
                                    </div>
                                </div>
                            )}
                    </div>
                    {rows.length > 0 && !loading && (
                        <div
                            style={{
                                padding: "14px 20px",
                                borderTop: "1px solid #eeeeee",
                                background: "#fff",
                                display: "flex",
                                justifyContent: "flex-end",
                            }}
                        >
                            <button
                                type="button"
                                className="btn btn-success"
                                disabled={finalizing}
                                onClick={handleFinalize}
                                style={{
                                    minWidth: "120px",
                                    height: "40px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "6px",
                                    borderRadius: "6px",
                                }}
                            >
                                {finalizing ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm"
                                            role="status"
                                        />
                                        Finalizing...
                                    </>
                                ) : (
                                    <>
                                        <i className="ti ti-check" />
                                        Finalize
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                    {showFinalizeModal && (
                        <div
                            style={{
                                position: "fixed",
                                inset: 0,
                                backgroundColor:
                                    "rgba(0, 0, 0, 0.45)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 9999,
                            }}
                        >
                            <div
                                style={{
                                    width: "420px",
                                    maxWidth: "90%",
                                    background: "#fff",
                                    borderRadius: "10px",
                                    boxShadow:
                                        "0 10px 30px rgba(0,0,0,0.15)",
                                    overflow: "hidden",
                                }}
                            >
                                {/* HEADER */}
                                <div
                                    style={{
                                        padding: "18px 20px",
                                        borderBottom:
                                            "1px solid #eeeeee",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "10px",
                                    }}
                                >
                                    <div
                                        style={{
                                            width: "38px",
                                            height: "38px",
                                            borderRadius: "50%",
                                            background: "#e6f4ea",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <i
                                            className="ti ti-check"
                                            style={{
                                                fontSize: "20px",
                                                color: "#198754",
                                            }}
                                        />
                                    </div>

                                    <div>
                                        <h5
                                            style={{
                                                margin: 0,
                                                fontSize: "16px",
                                                fontWeight: 600,
                                            }}
                                        >
                                            Finalize Selection
                                        </h5>
                                    </div>
                                </div>

                                {/* BODY */}
                                <div
                                    style={{
                                        padding: "22px 20px",
                                    }}
                                >
                                    <p
                                        style={{
                                            margin: 0,
                                            fontSize: "14px",
                                            color: "#555",
                                            lineHeight: "1.6",
                                        }}
                                    >
                                        Are you sure you want to
                                        finalize the class and
                                        orientation selections?
                                    </p>

                                    <div
                                        style={{
                                            marginTop: "12px",
                                            padding: "10px 12px",
                                            background: "#fffbe6",
                                            border:
                                                "1px solid #ffe58f",
                                            borderRadius: "6px",
                                            fontSize: "12px",
                                            color: "#8c6d1f",
                                        }}
                                    >
                                        <i
                                            className="ti ti-alert-circle"
                                            style={{
                                                marginRight: "6px",
                                            }}
                                        />
                                        Once finalized, please verify
                                        the selections before continuing.
                                    </div>
                                </div>

                                {/* FOOTER */}
                                <div
                                    style={{
                                        padding: "14px 20px",
                                        borderTop:
                                            "1px solid #eeeeee",
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        gap: "8px",
                                    }}
                                >
                                    {/* NO */}
                                    <button
                                        type="button"
                                        className="btn btn-light"
                                        disabled={finalizing}
                                        onClick={() =>
                                            setShowFinalizeModal(false)
                                        }
                                        style={{
                                            minWidth: "80px",
                                            height: "38px",
                                            border:
                                                "1px solid #d9d9d9",
                                            borderRadius: "6px",
                                        }}
                                    >
                                        No
                                    </button>

                                    {/* YES */}
                                    <button
                                        type="button"
                                        className="btn btn-success"
                                        disabled={finalizing}
                                        onClick={handleFinalize}
                                        style={{
                                            minWidth: "110px",
                                            height: "38px",
                                            borderRadius: "6px",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: "6px",
                                        }}
                                    >
                                        {finalizing ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm"
                                                    role="status"
                                                />
                                                Finalizing...
                                            </>
                                        ) : (
                                            <>
                                                <i className="ti ti-check" />
                                                Yes, Finalize
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default WizklubBranchClassOrientation;

